/**
 * Analytics Utility
 * Centralized analytics tracking using PostHog
 */

// Check if PostHog is available
const getPostHog = () => {
  if (typeof window !== 'undefined' && window.posthog) {
    return window.posthog;
  }
  return null;
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} properties - Additional properties to track
 */
export const trackEvent = (eventName, properties = {}) => {
  const posthog = getPostHog();
  if (posthog) {
    posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Identify a user (call after login)
 * @param {string} userId - User's unique ID
 * @param {object} traits - User traits (email, name, etc.)
 */
export const identifyUser = (userId, traits = {}) => {
  const posthog = getPostHog();
  if (posthog) {
    posthog.identify(userId, {
      ...traits,
      identified_at: new Date().toISOString(),
    });
  }
};

/**
 * Reset user identity (call on logout)
 */
export const resetUser = () => {
  const posthog = getPostHog();
  if (posthog) {
    posthog.reset();
  }
};

/**
 * Set user properties
 * @param {object} properties - Properties to set
 */
export const setUserProperties = (properties) => {
  const posthog = getPostHog();
  if (posthog) {
    posthog.people.set(properties);
  }
};

// Pre-defined event names for consistency
export const EVENTS = {
  // Authentication
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  
  // Resume Events
  RESUME_CREATED: 'resume_created',
  RESUME_UPDATED: 'resume_updated',
  RESUME_DELETED: 'resume_deleted',
  RESUME_DOWNLOADED: 'resume_downloaded',
  RESUME_TEMPLATE_SELECTED: 'resume_template_selected',
  
  // Cover Letter Events
  COVER_LETTER_CREATED: 'cover_letter_created',
  COVER_LETTER_GENERATED: 'cover_letter_generated',
  COVER_LETTER_DOWNLOADED: 'cover_letter_downloaded',
  
  // Job Search Events
  JOB_SEARCH_PERFORMED: 'job_search_performed',
  JOB_SAVED: 'job_saved',
  JOB_APPLIED: 'job_applied',
  
  // Feature Usage
  INTERVIEW_PREP_STARTED: 'interview_prep_started',
  SALARY_ANALYSIS_REQUESTED: 'salary_analysis_requested',
  ATS_CHECK_PERFORMED: 'ats_check_performed',
  CAREER_PLAN_CREATED: 'career_plan_created',
  ASSESSMENT_COMPLETED: 'assessment_completed',
  
  // AI Features
  AI_ASSISTANT_USED: 'ai_assistant_used',
  AI_CONTENT_GENERATED: 'ai_content_generated',
  
  // Engagement
  PAGE_VIEWED: 'page_viewed',
  FEATURE_CLICKED: 'feature_clicked',
  HELP_CENTER_VISITED: 'help_center_visited',
  CONTACT_FORM_SUBMITTED: 'contact_form_submitted',
  
  // Subscription
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  
  // Errors
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
};

/**
 * Track page view
 * @param {string} pageName - Name of the page
 * @param {object} properties - Additional properties
 */
export const trackPageView = (pageName, properties = {}) => {
  trackEvent(EVENTS.PAGE_VIEWED, {
    page_name: pageName,
    url: window.location.href,
    referrer: document.referrer,
    ...properties,
  });
};

/**
 * Track error
 * @param {Error} error - The error object
 * @param {string} context - Where the error occurred
 */
export const trackError = (error, context = 'unknown') => {
  trackEvent(EVENTS.ERROR_OCCURRED, {
    error_message: error.message,
    error_stack: error.stack,
    context,
    url: window.location.href,
  });
};

/**
 * Track API error
 * @param {string} endpoint - API endpoint
 * @param {number} statusCode - HTTP status code
 * @param {string} errorMessage - Error message
 */
export const trackApiError = (endpoint, statusCode, errorMessage) => {
  trackEvent(EVENTS.API_ERROR, {
    endpoint,
    status_code: statusCode,
    error_message: errorMessage,
  });
};

export default {
  trackEvent,
  identifyUser,
  resetUser,
  setUserProperties,
  trackPageView,
  trackError,
  trackApiError,
  EVENTS,
};
